import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap } from '@atlaskit/css';
import { ErrorMessage, RequiredAsterisk } from '@atlaskit/form';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/core/cross';
import Link from '@atlaskit/link';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { isAtlassianEmployee } from '../../../utils/date-time-util/isAtlassianEmployee';

import messages from './messages';

const styles = cssMap({
	bannerContainer: {
		marginTop: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy';

export interface AUPFeedbackBannerProps {
	isVisible: boolean;
	onSubmit: (data: {
		feedbackText: string;
		includeInput: boolean;
		enrollInResearch: boolean;
	}) => void | Promise<void>;
	onDismiss: () => void;
	userEmail?: string;
	shouldHideCheckboxes?: boolean;
}

type ThankYouMessageProps = {
	onDismiss: () => void;
};

const ThankYouMessage = ({ onDismiss }: ThankYouMessageProps) => {
	const { formatMessage } = useIntl();
	return (
		<Inline space="space.100" alignBlock="center" spread="space-between">
			<Inline space="space.100" alignBlock="center">
				<CheckCircleIcon label="" color={token('color.icon.success')} />
				<Text weight="medium" color="color.text.subtle">
					{formatMessage(messages.thankYouMessage)}
				</Text>
			</Inline>
			<IconButton
				appearance="subtle"
				icon={CrossIcon}
				label={formatMessage(messages.dismissBanner)}
				onClick={onDismiss}
				spacing="compact"
				isTooltipDisabled={false}
			/>
		</Inline>
	);
};

export const AUPFeedbackBanner = (props: AUPFeedbackBannerProps): React.JSX.Element => {
	const { isVisible, onSubmit, onDismiss, userEmail, shouldHideCheckboxes } = props;
	const { formatMessage } = useIntl();
	const [feedbackText, setFeedbackText] = useState('');

	const hideCheckboxes = shouldHideCheckboxes ?? false;

	const [includeInput, setIncludeInput] = useState<boolean>(isAtlassianEmployee(userEmail));
	const [enrollInResearch, setEnrollInResearch] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [showValidationError, setShowValidationError] = useState(false);
	const handleSubmit = useCallback(() => {
		if (!feedbackText.trim()) {
			setShowValidationError(true);
			return;
		}
		onSubmit({ feedbackText, includeInput, enrollInResearch });
		setHasSubmitted(true);
	}, [onSubmit, feedbackText, includeInput, enrollInResearch]);

	return (
		<ExitingPersistence>
			{isVisible && (
				<FadeIn exitDirection="top" duration="large">
					{(motionProps) => (
						<div {...motionProps}>
							<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
								{hasSubmitted && formatMessage(messages.thankYouMessage)}
							</VisuallyHidden>
							<Box xcss={styles.bannerContainer}>
								{hasSubmitted ? (
									<ThankYouMessage onDismiss={onDismiss} />
								) : (
									<Stack space="space.200">
										<Inline space="space.100" alignBlock="center" spread="space-between">
											<Text weight="medium" color="color.text.subtle">
												{formatMessage(messages.incorrectFlagTitle)}
											</Text>
											<IconButton
												appearance="subtle"
												icon={CrossIcon}
												label={formatMessage(messages.dismissBanner)}
												onClick={onDismiss}
												spacing="compact"
												isTooltipDisabled={false}
											/>
										</Inline>

										<Stack space="space.100">
											<Text size="small" color="color.text.subtlest" weight="bold">
												{formatMessage(messages.incorrectFlagLabel)} <RequiredAsterisk />
											</Text>
											<TextArea
												placeholder={formatMessage(messages.incorrectFlagTextareaLabel)}
												value={feedbackText}
												onChange={(e) => {
													setFeedbackText(e.target.value);
													setShowValidationError(false);
												}}
												minimumRows={2}
												aria-label={formatMessage(messages.incorrectFlagTextareaLabel)}
												aria-required={true}
												aria-invalid={showValidationError}
											/>
											{showValidationError && (
												<ErrorMessage>{formatMessage(messages.feedbackTextRequired)}</ErrorMessage>
											)}
										</Stack>

										{!hideCheckboxes && (
											<>
												<Checkbox
													label={formatMessage(messages.includeInputCheckbox)}
													isChecked={includeInput}
													onChange={(e) => setIncludeInput(e.target.checked)}
												/>

												<Checkbox
													label={
														<>
															{formatMessage(messages.enrollInResearchCheckbox, {
																privacyPolicyLink: (
																	<Link href={PRIVACY_POLICY_URL} target="_blank">
																		{formatMessage(messages.privacyPolicyLink)}
																	</Link>
																),
															})}
														</>
													}
													isChecked={enrollInResearch}
													onChange={(e) => setEnrollInResearch(e.target.checked)}
												/>
											</>
										)}

										<Button appearance="primary" onClick={handleSubmit} shouldFitContainer>
											{formatMessage(messages.submitButton)}
										</Button>
									</Stack>
								)}
							</Box>
						</div>
					)}
				</FadeIn>
			)}
		</ExitingPersistence>
	);
};
